import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { apiParameters } from 'src/app/configs/api.params';
import { IUser } from '../models/user.model';
import { map } from 'rxjs/operators';
import { IVehicles } from '../models/vehicle.model';
import { ErrorService } from './error.service';
import { IError } from '../models/error.model';

@Injectable({
  providedIn: 'root'
})

export class ClientService {

  constructor(
    private httpClient: HttpClient,
    private errorService: ErrorService) {}

  /**
   * This method gets a list of client administrators
   * @param clientId - client id
   * @returns Client admins 
   */
  getClientAdmins(clientId: string, page?: number, limit?: number,): Observable<any> {
     let httpOptions = {
        headers: new HttpHeaders({
          page: page.toString(),
          limit: limit.toString(),
        })}

    return this.httpClient.get(`${apiParameters.urlV2}/clients/${clientId}/admins`, httpOptions);
  }

  /**
   * Gets one client admin
   * @param clientId - client id
   * @param adminId - admin id
   * @returns One client admin 
   */
  getOneClientAdmin(clientId, adminId): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/clients/${clientId}/admins/${adminId}`);
  }

  /**
   * Gets client settings
   * @param clientId - client id
   * @returns Client settings 
   */
  getClientSettings(clientId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/clients/${clientId}/settings`);
  }

  updateClientSettings(clientId: string, body): Observable<any> {
    return this.httpClient.patch(`${apiParameters.url}/clients/${clientId}/settings`, body);
  }

  createClientAdmin(clientId: string, body): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/clients/${clientId}/admins`, body);
  }

  updateClientAdmin(clientId: string, adminId, body): Observable<any> {
    return this.httpClient.patch(`${apiParameters.urlV2}/clients/${clientId}/admins/${adminId}`, body);
  }

  deleteClientAdmin(clientId: string, adminId): Observable<any> {
    return this.httpClient.delete(`${apiParameters.urlV2}/clients/${clientId}/admins/${adminId}`);
  }

  /**
   * Gets all vehicles
   * @param clientId - client id
   * @param page - number of page for pagination
   * @param limit - items to show for page
   * @param vins - optional query filter vins name
   * @param make - optional query filter
   * @param from - optional query filter from date
   * @param to - optional query filter to date
   * @param processingStatus - optional query filter
   * @returns all vehicles 
   */
  getAllVehicles(clientId, page: number, limit: number, withoutCount: boolean, vins: string[], make: string, from: string, to: string, processingStatus: string, exact?: any, licensePlateNumber?: any): Observable<any> {
    let searchParams = new HttpParams();

    if (vins && vins.length > 0) {
      vins.forEach(vin => {
        searchParams = searchParams.append('vin[]', vin)
      });
    }
    if (make && make !== '') {
      searchParams = searchParams.append('make', make);
    }
    if (from && from !== '') {
      searchParams = searchParams.append('from', from);
    }
    if (to && to !== '') {
      searchParams = searchParams.append('to', to);
    }

    if (processingStatus && processingStatus !== '') {
      searchParams = searchParams.append('vehicleStatus', processingStatus);
    }

    if (exact && exact !== '') {
      searchParams = searchParams.append('exact', exact)
    }
    if(licensePlateNumber && licensePlateNumber !== '') {
      searchParams = searchParams.append('licensePlateNumber', licensePlateNumber);
    }
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString(),
        // withoutCount: withoutCount.toString(),
      }),
      params: searchParams
    };

    return this.httpClient.get(`${apiParameters.url}/clients/${clientId}/vehicles`, httpOptions)
      .pipe(
        map((res:any) => (
          {
            count: res.count || null,
            results: res.results || res
          }
        )),
        map((res: IVehicles) => {
          res.results.sort((a, b) => {
            return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
          });
          return res;
        })
      );
  }

  getVehiclesOfUser(clientId, userId, page: number, limit: number, withoutCount: boolean, vin: string, make: string, from: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString(),
        // withoutCount: withoutCount.toString(),
      })
    };

    let vinSearch = '';
    if (vin && vin !== '') {
      vinSearch = '&vin=' + vin;
    }
    let makeSearch = '';
    if (make && make !== '') {
      makeSearch = '&make=' + make;
    }
    let fromDate = '';
    if (from && from !== '') {
      fromDate = '&from=' + fromDate;
    }

    return this.httpClient.get(`${apiParameters.url}/clients/${clientId}/users/${userId}/vehicles?${vinSearch}${fromDate}`, httpOptions)
      .pipe(
        map((res:any) => (
          {
            count: res.count || null,
            results: res.results || res
          }
        )),
        map((res: IVehicles) => {
          // return res;
          res.results.sort((a, b) => {
            return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
          });
          return res;
        })
      );
  }

  /**
   * Adds new user as client
   * @param body - user data
   * @param clientId - client id
   * @returns User 
   */
  addUser(body, clientId): Observable<IUser> {
    return this.httpClient.post(apiParameters.url + '/clients/' + clientId + '/users', body)
      .pipe(map((res: IUser) => {
        return res;
      }));
  }

  addCreditToClient(clientId, body): Observable<IUser> {
    return this.httpClient.post(apiParameters.url + '/clients/' + clientId + '/transfer-credits', body)
      .pipe(
        map((res: IUser) => {
          return res;
        })
      );
  }

  getLicensePlates(clientId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/clients/${clientId}/license-plates`);
  }

  addLicensePlate(clientId: string, body): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/clients/${clientId}/license-plates`, body);
  }

  removeLicensePlate(clientId: string, plateId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.url}/clients/${clientId}/license-plates/${plateId}`);
  }

  createInvoice(clientId: string): Observable<any> {
    return this.httpClient.post(`${apiParameters.url}/clients/${clientId}/invoices`, {});
  }

  getInvoices(clientId: string, page: number, limit: number): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      })
    };

    return this.httpClient.get(`${apiParameters.url}/clients/${clientId}/invoices`, httpOptions)
      .pipe(
        map((res: any) => {
          res.results.sort((a, b) => {
            return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
          });
          return res;
        })
      );
  }

  /**
   * Gets client invoice by id
   * @param clientId - client id
   * @param invoiceId - invoice id
   * @returns Invoice by id 
   */
  getInvoiceById(clientId: string, invoiceId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/clients/${clientId}/invoices/${invoiceId}`);
  }

  getInvoiceItems(clientId: string, invoiceId: string, page: number, limit: number, userId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (userId && userId !== '') {
      searchParams = searchParams.append('userId', userId);
    }
    
    const httpOptions = {
      headers: new HttpHeaders({
        page: page.toString(),
        limit: limit.toString()
      }),
      params: searchParams
    };

    return this.httpClient.get(`${apiParameters.url}/clients/${clientId}/invoices/${invoiceId}/items`, httpOptions);
  }

  /**
   * Gets invoice csv
   * @param clientId - client id
   * @param invoiceId - invoice id
   * @returns invoice csv 
   */
  getInvoiceCSV(clientId: string, invoiceId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.url}/clients/${clientId}/invoices/${invoiceId}/csv`, { responseType: 'text' });
  }

  /**
   * Generates csv
   * @param clientId - client id
   * @param invoiceId - invoice id
   * @returns csv 
   */
  generateCSV(clientId: string, invoiceId: string, customInvoiceConfigEnable?: string): Observable<any> {
    let params = new HttpParams();

    if(customInvoiceConfigEnable) {
      params = params.append('customInvoiceConfigEnable',  customInvoiceConfigEnable);
    }
    
    const httpOptions = {
      params: params
    };
    return this.httpClient.get(`${apiParameters.url}/clients/${clientId}/invoices/${invoiceId}/csv`, httpOptions);
  }

  payInvoice(clientId: string, invoiceId: string, amount?: number): Observable<any> {
    return this.httpClient.patch(`${apiParameters.url}/clients/${clientId}/invoices/${invoiceId}/pay`, { 'amount': amount });
  }

  closeInvoice(clientId: string, invoiceId: string): Observable<any> {
    return this.httpClient.patch(`${apiParameters.url}/clients/${clientId}/invoices/${invoiceId}/close`, {});
  }

  changeCustomer(clientId: string, body: any): Observable<any> {
    return this.httpClient.patch(`${apiParameters.url}/clients/${clientId}/change-customer`, body);
  }

  createVehicleType(clientId: string, body: any): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/clients/${clientId}/vehicle-types`, body);
  }

  editVehicleType(clientId: string, typeId: string, body: any): Observable<any> {
    return this.httpClient.patch(`${apiParameters.urlV2}/clients/${clientId}/vehicle-types/${typeId}`, body);
  }

  deleteVehicleType(clientId: string, typeId:string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.urlV2}/clients/${clientId}/vehicle-types/${typeId}`);
  }

  getVehicleTypes(clientId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/clients/${clientId}/vehicle-types`);
  }

  getClientSHHKeys(clientId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/clients/${clientId}/ssh-keys`);
  }

  getClientSHHKey(clientId: string, keyId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/clients/${clientId}/ssh-keys/${keyId}`);
  }

  createClientSHHKey(clientId: string, body): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/clients/${clientId}/ssh-keys`, body);
  }

  deleteClientSHHKey(clientId: string, keyId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.urlV2}/clients/${clientId}/ssh-keys/${keyId}`);
  }

  createPlaceholder(clientId: string, body: FormData): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders`, body);
  }

  updatePlaceholder(clientId: string, textPlaceholderId: string, body: FormData): Observable<any> {
    return this.httpClient.patch(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders/${textPlaceholderId}`, body);
  }

  deletePlaceholder(clientId: string, textPlaceholderId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders/${textPlaceholderId}`);
  }

  getPlaceholders(clientId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders`);
  }

  getPlaceholderById(clientId: string, placeholderId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders/${placeholderId}`);
  }

  assignPlaceholderToTarget(clientId: string, placeholderId: string, presetTargetId: string): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders/${placeholderId}/preset-targets/${presetTargetId}`, {});
  }

  deleteFontFile(clientId: string, textPlaceholderId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders/${textPlaceholderId}/font-file`);
  }

  createField(clientId: string, textPlaceholderId: string, body: any): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders/${textPlaceholderId}/fields`, body);
  }

  updateField(clientId: string, textPlaceholderId: string, fieldId: string, body: any): Observable<any> {
    return this.httpClient.patch(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders/${textPlaceholderId}/fields/${fieldId}`, body);
  }

  deleteField(clientId: string, textPlaceholderId: string, fieldId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.urlV2}/clients/${clientId}/text-placeholders/${textPlaceholderId}/fields/${fieldId}`);
  }

  getFillableImages(clientId: string): Observable<any> {
    return this.httpClient.get(`${apiParameters.urlV2}/clients/${clientId}/fillable-images`);
  }

  deleteFillableImage(clientId: string, imageId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.urlV2}/clients/${clientId}/fillable-images/${imageId}`);
  }

  createFillableImage(clientId: string, body: FormData): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/clients/${clientId}/fillable-images`, body);
  }

  editFillableImage(clientId: string, imageId, body: any): Observable<any> {
    return this.httpClient.patch(`${apiParameters.urlV2}/clients/${clientId}/fillable-images/${imageId}`, body);
  }

  assignFillableImageToTarget(clientId: string, imageId: string, presetTargetId: string): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/clients/${clientId}/fillable-images/${imageId}/preset-targets/${presetTargetId}`, {});
  }

  detachFillableImageFromTarget(clientId: string, imageId: string, presetTargetId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.urlV2}/clients/${clientId}/fillable-images/${imageId}/preset-targets/${presetTargetId}`, {});
  }

  updatePresetTargetConfig(clientId: string, presetTargetId: string, body): Observable<any> {
    return this.httpClient.put(`${apiParameters.url}/clients/${clientId}/preset-targets/${presetTargetId}/configurations`, body);
  }

  deletePresetTargetConfig(clientId: string, presetTargetId: string): Observable<any> {
    return this.httpClient.delete(`${apiParameters.url}/clients/${clientId}/preset-targets/${presetTargetId}/configurations`);
  }

  private handleError(errorRes: HttpErrorResponse) {
    let error = {} as IError;
    error = this.errorService.whichError(errorRes);
    return throwError(error);
  }

}
